/**
 * `skaile cache` — inspect and reclaim the re-derivable cache.
 *
 * Scopes: project `.skaile/cache/` (default) and the global cache root
 * (`--global`). NEVER touches authored content (`~/.skaile/sidecars`,
 * `libraries/`) or state (`index.db`, sessions, logs, lock, patches).
 */
import { existsSync, lstatSync, readdirSync, rmSync, statSync } from "node:fs";
import { homedir } from "node:os";
import path from "node:path";
import * as p from "@clack/prompts";
import { globalCacheRoot } from "@skaile/workspaces/core";
import { Command } from "commander";
import { logInfo, logOk, S } from "../helpers.ts";

interface CacheScope {
  projectDir: string;
  global?: boolean;
}

/** All cache directories in scope, in deletion order. Pure — no I/O side effects. */
export function collectCacheTargets(scope: CacheScope): string[] {
  const out: string[] = [];
  out.push(path.join(scope.projectDir, ".skaile", "cache"));
  // Legacy project dirs (pre-consolidation) — swept so upgrades reclaim disk.
  out.push(path.join(scope.projectDir, ".skaile", "store-cache"));
  out.push(path.join(scope.projectDir, ".skaile", "repos"));
  if (scope.global) {
    out.push(globalCacheRoot()); // ~/.skaile/cache (sources + catalog)
    const home = process.env.SKAILE_HOME ?? path.join(homedir(), ".skaile");
    out.push(path.join(home, "repos")); // legacy clone root
    out.push(path.join(home, "catalog-cache")); // legacy catalog cache
  }
  return out;
}

/**
 * Recursive byte size of a path (0 if absent). Uses `lstat` and does NOT follow
 * symlinks — project `.skaile/cache/sources/<name>` entries are symlinks into the
 * global cache, so following them would double-count (and a symlink cycle would
 * loop). Symlinks contribute 0; the global cache is sized directly.
 */
export function dirSize(target: string): number {
  if (!existsSync(target)) return 0;
  const st = lstatSync(target);
  if (st.isSymbolicLink()) return 0;
  if (!st.isDirectory()) return st.size;
  return dirSizeOfDir(target);
}

/** Sum a known directory's contents — one `readdir` per level (withFileTypes),
 * `stat` only for regular files, symlinks skipped (never followed). */
function dirSizeOfDir(dir: string): number {
  let total = 0;
  for (const ent of readdirSync(dir, { withFileTypes: true })) {
    const child = path.join(dir, ent.name);
    if (ent.isSymbolicLink()) continue;
    if (ent.isDirectory()) total += dirSizeOfDir(child);
    else if (ent.isFile()) total += statSync(child).size;
  }
  return total;
}

/** Delete the targets that exist. Returns the ones actually removed (empty in dry-run). */
export function removeCacheTargets(targets: string[], opts: { dryRun: boolean }): string[] {
  const present = targets.filter((t) => existsSync(t));
  if (opts.dryRun) return [];
  for (const t of present) rmSync(t, { recursive: true, force: true });
  return present;
}

function fmtBytes(n: number): string {
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

/**
 * Build the `skaile cache` command group: `clean` (reclaims project cache, or
 * project+global with `--global`; confirms unless `--yes`; `--dry-run` previews),
 * `dir` (prints resolved cache roots), and `size` (per-dir disk usage). Never
 * targets authored content or state — see {@link collectCacheTargets}.
 */
export function makeCacheCommand(): Command {
  const cmd = new Command("cache").description("Inspect and reclaim the re-derivable cache");

  cmd
    .command("clean")
    .description("Remove the project cache (.skaile/cache); --global also clears ~/.skaile/cache")
    .option("--global", "Also clear the global cache (sources + catalog) and legacy dirs")
    .option("--dry-run", "Show what would be removed without doing it")
    .option("-y, --yes", "Skip confirmation prompt")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (opts) => {
      const targets = collectCacheTargets({
        projectDir: path.resolve(opts.projectDir),
        global: opts.global,
      }).filter((t) => existsSync(t));

      if (targets.length === 0) {
        logInfo("Cache is already empty.");
        return;
      }
      console.log();
      console.log(`  ${S.heading("Will remove:")}`);
      for (const t of targets)
        console.log(`    ${S.dim(t)}  ${S.dim(`(${fmtBytes(dirSize(t))})`)}`);

      if (opts.dryRun) {
        console.log();
        logInfo("Dry run — no changes made.");
        return;
      }
      if (!opts.yes) {
        const ok = await p.confirm({
          message: opts.global ? "Clear project AND global cache?" : "Clear project cache?",
        });
        if (p.isCancel(ok) || !ok) {
          logInfo("Cancelled.");
          return;
        }
      }
      const removed = removeCacheTargets(targets, { dryRun: false });
      console.log();
      for (const r of removed) logOk(`Removed ${r}`);
    });

  cmd
    .command("dir")
    .description("Print the resolved cache roots")
    .option("--global", "Include the global cache root")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action((opts) => {
      console.log(path.join(path.resolve(opts.projectDir), ".skaile", "cache"));
      if (opts.global) console.log(globalCacheRoot());
    });

  cmd
    .command("size")
    .description("Show cache disk usage per directory")
    .option("--global", "Include the global cache")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action((opts) => {
      const targets = collectCacheTargets({
        projectDir: path.resolve(opts.projectDir),
        global: opts.global,
      });
      console.log();
      for (const t of targets) {
        if (!existsSync(t)) continue;
        console.log(`  ${S.dim(fmtBytes(dirSize(t)).padStart(9))}  ${t}`);
      }
      console.log();
    });

  return cmd;
}
